import posthog from 'posthog-js';
import { isPostHogInitialized } from '@/instrumentation-client';
import type { CanvasCreationSurface } from '@/lib/canvas/constants';
import type { HostConnector } from '@/lib/db/schema';
import { type AnalyticsPlatform, getClientAnalyticsPlatform } from './platform';

/** Supported aspect ratios for rendering analytics */
export const renderingAspectRatios = [
  'original',
  '1:1',
  '16:9',
  '9:16',
  '4:3',
  '3:2',
] as const;

export type RenderingAspectRatio = (typeof renderingAspectRatios)[number];
export type RenderingBaseImageSource = 'upload' | 'revit_capture' | 'canvas';
export type RenderingAnalyticsSurface = 'rendering' | 'canvas';
export type RenderingAnalyticsOperationType = 'generate' | 'edit';

export function getSafeRenderingAspectRatio(
  aspectRatio: unknown,
): RenderingAspectRatio {
  return renderingAspectRatios.includes(aspectRatio as RenderingAspectRatio)
    ? (aspectRatio as RenderingAspectRatio)
    : 'original';
}

type RenderingGenerateStartedProperties = {
  surface: RenderingAnalyticsSurface;
  operationType: 'generate';
  aspectRatio: RenderingAspectRatio;
  promptLength: number;
  hasReferenceImage: boolean;
  baseImageSource: RenderingBaseImageSource;
  mode: 'normal' | 'transparent';
};

type RenderingEditStartedProperties = {
  surface: 'canvas';
  operationType: 'edit';
  renderingId: string;
  promptLength: number;
  baseImageSource: 'canvas';
  hasInpaintMask: boolean;
};

type RenderingOutcomeProperties = {
  renderingId: string;
  surface: RenderingAnalyticsSurface;
  operationType: RenderingAnalyticsOperationType;
  hasInpaintMask?: boolean;
};

type BillingLifecycleProperties = {
  organizationId: string;
  organizationName: string;
  userEmail?: string;
  userName?: string;
  jurisdiction: string;
  companyRole?: string;
  billingSource: 'self_serve' | 'enterprise';
  organizationPlanId: 'trial' | 'pro' | 'enterprise';
  billingInterval: 'month' | 'year' | 'contract';
  seatCount: number;
  unitAmountMinor: number | null;
  currency: string | null;
  providerStatus: string;
};

/**
 * Analytics event map.
 * Single source of truth for all event names and their properties.
 */
export interface AnalyticsEventMap {
  // Communication deliverability. Recipient addresses are never included.
  transactional_email_delivery_failed: {
    messageKey: string;
    provider: 'resend';
    outcome:
      | 'hard_bounce'
      | 'soft_bounce'
      | 'provider_suppressed'
      | 'provider_error';
    recipientHash: string;
    hasLinkedUser: boolean;
    userRequested: boolean;
  };
  transactional_email_complained: {
    messageKey: string;
    provider: 'resend';
    recipientHash: string;
    hasLinkedUser: boolean;
    userRequested: boolean;
  };
  transactional_email_delivery_restored: {
    provider: 'resend';
    recipientHash: string;
    hasLinkedUser: boolean;
    suppressionReasons: string[];
    restoredCount: number;
  };
  transactional_email_delivery_blocked_again: {
    messageKey: string;
    provider: 'resend';
    outcome: 'hard_bounce' | 'complaint' | 'provider_suppressed';
    recipientHash: string;
    hasLinkedUser: boolean;
    userRequested: boolean;
  };
  // Onboarding
  enterprise_pass_activated: {
    grantId: string;
    organizationId: string;
    organizationName: string;
    organizationPlanId: 'enterprise';
    userEmail: string;
    userName?: string;
    jurisdiction: string;
    companyRole?: string;
    campaignId: string;
    campaignName: string;
    redemptionPath: 'new_signup' | 'existing_user';
    startsAt: string;
    endsAt: string;
    durationDays: number;
  };
  user_signed_up: {
    method: 'email' | 'google' | 'microsoft';
    organizationId: string;
    flow?: 'self_serve_signup';
    organizationPlanId?: 'free' | 'pro' | 'enterprise';
    signupFlowId?: string;
  };
  signup_step_completed: {
    signupFlowId: string;
    step: 'identity' | 'password' | 'email_verified' | 'profile' | 'plan';
    authMethod?: 'email' | 'oauth';
    nextStep: string;
    organizationPlanId?: 'pro';
  };
  signup_step_failed: {
    signupFlowId?: string;
    step:
      | 'identity'
      | 'password'
      | 'email_verified'
      | 'profile'
      | 'plan'
      | 'unknown';
    reason: string;
  };
  signup_verification_resent: { signupFlowId: string };
  project_created: { projectId: string };
  canvas_created: {
    canvasId: string;
    organizationId?: string;
    projectId?: string;
    attemptId?: string;
    surface?: CanvasCreationSurface | 'unknown';
    currentCanvasCount?: number;
    limit?: number;
  };
  canvas_creation_attempted: {
    attemptId: string;
    surface: CanvasCreationSurface;
    projectId?: string;
    ownedCanvasCount?: number;
    limit: number;
  };
  canvas_creation_blocked: {
    attemptId: string;
    surface: CanvasCreationSurface;
    projectId?: string;
    ownedCanvasCount?: number;
    reason: 'workspace_access' | 'canvas_limit';
    limit: number;
  };
  canvas_creation_failed: {
    attemptId: string;
    surface: CanvasCreationSurface | 'unknown';
    organizationId?: string;
    projectId?: string;
    currentCanvasCount?: number;
    reason:
      | 'workspace_access'
      | 'validation_error'
      | 'canvas_limit'
      | 'project_not_found'
      | 'service_unavailable'
      | 'unexpected_error'
      | 'network_error'
      | 'invalid_response';
    source: 'client' | 'server';
    statusCode?: number;
    limit: number;
  };
  canvas_connection_auth: {
    canvasId?: string;
    organizationId?: string;
    outcome: 'succeeded' | 'failed';
    reason:
      | 'authorized'
      | 'workspace_access'
      | 'validation_error'
      | 'canvas_access_denied'
      | 'permission_lookup_error'
      | 'liveblocks_authorization_error'
      | 'liveblocks_rejected';
    statusCode: number;
    durationMs: number;
  };
  canvas_connection_state: {
    canvasId: string;
    outcome: 'connected' | 'failed' | 'timed_out';
    durationMs: number;
    status:
      | 'initial'
      | 'connecting'
      | 'connected'
      | 'reconnecting'
      | 'disconnected';
    errorType?: string;
    errorCode?: number;
  };
  canvas_revit_capture: {
    outcome: 'succeeded' | 'failed';
    errorCode?:
      | 'revit_request_in_progress:api'
      | 'unsupported_plugin_version:api'
      | 'timeout'
      | 'capture_failed';
  };
  document_uploaded: { projectId: string; fileType: string; fileSize: number };

  // Core Engagement
  chat_message_sent: {
    chatId: string;
    messageLength: number;
    platform?: AnalyticsPlatform;
  };
  chat_response_started: {
    chatId: string;
    latencyMs: number;
  };
  chat_response_completed: {
    chatId: string;
    requestId: string;
    durationMs: number;
    status: 'success' | 'retryable' | 'failed';
    inputTokens?: number;
    outputTokens?: number;
    cachedInputTokens?: number;
    cacheCreationInputTokens?: number;
    responseModelId?: string;
    finalProvider?: string;
  };
  chat_citation_clicked: {
    chatId: string;
    documentId: string;
    chunkId?: string;
  };
  chat_citation_card_clicked: {
    chatId: string;
    documentId: string;
    chunkId: string;
    anchor: string;
  };
  chat_legal_citation_clicked: {
    chatId: string;
    caseId: string;
    court: string;
    referenceNumber: string | null;
  };
  chat_legal_citation_card_clicked: {
    chatId: string;
    caseId: string;
    court: string;
  };
  chat_grouped_citation_clicked: {
    chatId: string;
    documentId: string;
    groupNumber: number;
    citationCount: number;
  };
  chat_agent_max_steps_reached: {
    chatId: string;
    toolCallCount: number;
  };
  chat_context_overflow: {
    chatId: string;
    requestId: string;
    modelId: string;
    estimatedContextTokens: number;
    maxStepInputTokens: number;
    maxStepOutputTokens: number;
    wasCompacted: boolean;
    tokensSaved: number;
    partsCompacted: number;
    statusCode?: number;
  };
  assistant_skill_invoked: {
    chatId?: string;
    skillName: string;
    skillVersion: string;
    skillSource: string;
    requestClass:
      | 'active_plan_build'
      | 'plan_mode'
      | 'revit'
      | 'rhino'
      | 'archicad'
      | 'document_grounded'
      | 'project'
      | 'general';
    invocationResult: 'loaded' | 'blocked';
    blockReason?:
      | 'not_found'
      | 'disabled'
      | 'jurisdiction_blocked'
      | 'malformed'
      | 'oversized'
      | 'load_error';
    instructionBytes?: number;
    referencedFileCount?: number;
    limitCode?: string;
    limit?: number;
    actual?: number;
    jurisdiction?: string | null;
    jurisdictionSource?: 'project' | 'organization' | null;
    allowedJurisdictions?: string[];
    ignoredInferredJurisdiction?: string | null;
  };

  // Navigation
  project_opened: { projectId: string };

  // Friction
  error_displayed: { errorType: string; message?: string };

  // Reviews
  review_submitted: { rating: number; hasReviewText: boolean };
  review_skipped: Record<string, never>;

  // Onboarding
  onboarding_dialog_shown: { videoKey: string };
  onboarding_dialog_completed: { videoKey: string; viewDuration: number };
  onboarding_dialog_skipped: { videoKey: string; viewDuration: number };

  // Rendering
  rendering_started:
    | RenderingGenerateStartedProperties
    | RenderingEditStartedProperties;
  rendering_imported: {
    renderingId: string;
    source: 'preview_drop' | 'preview_click';
    fileSize: number;
  };
  rendering_completed: RenderingOutcomeProperties & {
    durationMs: number;
    variationCount: number;
  };
  rendering_failed: RenderingOutcomeProperties & {
    errorType: string;
  };
  rendering_downloaded: {
    renderingId: string;
    source: 'preview' | 'history' | 'dialog' | 'share';
  };
  rendering_deleted: {
    renderingId: string;
    source: 'preview' | 'history';
  };
  rendering_variation_deleted: {
    variationId: string;
    renderingId: string;
    parentDeleted: boolean;
  };
  rendering_variation_selected: {
    renderingId: string;
    variationIndex: number;
  };
  rendering_iterated: {
    renderingId: string;
    promptLength: number;
    hasInpaintMask: boolean;
  };
  rendering_shared: {
    variationId: string;
  };
  rendering_share_viewed: {
    variationId: string;
  };

  // Checklists
  checklist_created: {
    checklistId?: string; // Optional when multiple checklists created from templates
    projectId: string;
    source: 'scratch' | 'template';
    templateCount: number;
  };
  checklist_deleted: {
    checklistId: string;
    projectId: string;
  };
  checklist_exported: {
    projectId: string;
    checklistCount: number;
    format: 'pdf' | 'xlsx';
  };
  checklist_template_saved: {
    checklistId: string;
    isUpdate: boolean;
  };
  check_item_created: {
    checklistId: string;
    projectId: string;
    isHeader: boolean;
  };
  check_item_deleted: {
    checklistId: string;
    projectId: string;
    itemId: string;
  };
  check_item_status_changed: {
    checklistId: string;
    projectId: string;
    itemId: string;
    statusField: 'isIncluded' | 'todoStatus' | (string & {});
    oldStatus: string;
    newStatus: string;
    source: 'checkbox' | 'board';
  };
  check_item_comment_added: {
    checklistId: string;
    projectId: string;
    itemId: string;
    commentLength: number;
  };
  check_item_opened: {
    checklistId: string;
    projectId: string;
    itemId: string;
  };

  // Revit Plugin
  revit_plugin_downloaded: {
    version: string;
    source: 'downloads_page' | 'verification_step';
  };
  archicad_plugin_downloaded: {
    version: string;
    source: 'downloads_page' | 'verification_step';
    platform: 'windows' | 'macos';
  };
  rhino_plugin_downloaded: {
    version: string;
    source: 'downloads_page' | 'verification_step';
    platform: 'windows';
  };
  revit_plugin_download_cta_clicked: {
    source:
      | 'chat_update_banner'
      | 'chat_update_required_wall'
      | 'sidebar_banner';
  };
  archicad_plugin_download_cta_clicked: {
    source:
      | 'chat_update_banner'
      | 'chat_update_required_wall'
      | 'sidebar_banner';
  };
  rhino_plugin_download_cta_clicked: {
    source:
      | 'chat_update_banner'
      | 'chat_update_required_wall'
      | 'sidebar_banner';
  };
  revit_plugin_step_shown: Record<string, never>;
  revit_plugin_step_skipped: Record<string, never>;
  archicad_plugin_step_shown: Record<string, never>;
  archicad_plugin_step_skipped: Record<string, never>;
  rhino_plugin_step_shown: Record<string, never>;
  rhino_plugin_step_skipped: Record<string, never>;
  plugin_downloads_step_shown: Record<string, never>;
  plugin_downloads_step_skipped: Record<string, never>;
  plugin_downloads_page_opened: {
    source: 'verification_step';
  };

  // Enterprise booking handoff
  enterprise_booking_cta_clicked: {
    source:
      | 'admin_dashboard'
      | 'admin_billing'
      | 'admin_invitations'
      | 'admin_members'
      | 'sidebar_user_nav'
      | 'signup_onboarding';
    organizationType: 'personal' | 'team' | 'enterprise';
    organizationPlanId?: 'free' | 'pro' | 'enterprise';
    userRole: string;
    destinationKind: 'booking_url' | 'mailto_fallback';
  };

  pro_upgrade_intent: {
    source: 'sidebar_invite_people';
    currentPlan: 'grandfathered_free';
  };
  workspace_invitation_created: {
    organizationId: string;
    invitationType: 'admin-invitation' | 'superadmin-invitation';
    targetRole: 'member' | 'admin' | 'superadmin';
  };

  // Billing
  billing_checkout_started: {
    flow: 'self_serve_signup' | 'self_serve_upgrade';
    source: 'signup_checkout' | 'admin_billing';
    organizationType?: 'personal' | 'team' | 'enterprise';
    organizationPlanId?: 'free' | 'pro' | 'enterprise';
    retry: boolean;
    outcome: 'redirect' | 'already_completed' | 'already_pro' | 'error';
    signupFlowId?: string;
    billingInterval?: 'month' | 'year';
  };
  billing_checkout_resumed: {
    flow: 'self_serve_signup' | 'self_serve_upgrade';
    source: 'signup_checkout' | 'admin_billing';
    organizationType?: 'personal' | 'team' | 'enterprise';
    organizationPlanId?: 'free' | 'pro' | 'enterprise';
    viaSessionId: boolean;
    outcome: 'pending' | 'completed' | 'error';
    signupFlowId?: string;
    billingInterval?: 'month' | 'year';
  };
  billing_checkout_completed: {
    flow: 'self_serve_signup' | 'self_serve_upgrade';
    source: 'signup_checkout' | 'admin_billing';
    organizationId: string;
    organizationPlanId: 'pro';
    billingInterval: 'month' | 'year';
    startsTrial: boolean;
    signupFlowId?: string;
  };
  billing_trial_started: BillingLifecycleProperties;
  billing_subscription_activated: BillingLifecycleProperties & {
    convertedFromTrial: boolean;
  };
  billing_subscription_cancellation_scheduled: BillingLifecycleProperties;
  billing_subscription_canceled: BillingLifecycleProperties;
  billing_payment_failed: BillingLifecycleProperties & {
    amountMinor: number;
  };
  billing_portal_opened: {
    source: 'admin_billing';
    organizationType: 'personal' | 'team' | 'enterprise';
    organizationPlanId: 'free' | 'pro' | 'enterprise';
    outcome: 'redirect' | 'error';
  };
  billing_webhook_processed: {
    eventType: string;
    flow: 'self_serve_signup' | 'self_serve_upgrade' | 'lifecycle' | 'unknown';
    outcome:
      | 'processed'
      | 'ignored'
      | 'record_rejected'
      | 'sync_missing'
      | 'fulfillment_failed'
      | 'received'
      | 'duplicate'
      | 'retrying'
      | 'error';
  };

  // Revit one-click navigation chips (ENG-1086)
  revit_navigation_action: {
    actionKind: 'activate_view' | 'focus_elements' | 'focus_revision_clouds';
    chatId: string;
    ok: boolean;
    errorType?: string;
  };

  // Revit Tool Execution
  revit_tool_called: {
    toolName: string;
    requestId: string;
    chatId?: string;
    messageId?: string;
    toolCallId?: string;
    readOnly: boolean;
    isTemplate: boolean;
  };
  revit_tool_completed: {
    toolName: string;
    requestId: string;
    chatId?: string;
    messageId?: string;
    toolCallId?: string;
    success: boolean;
    readOnly: boolean;
    approvalStatus:
      | 'not_required'
      | 'pending'
      | 'approved'
      | 'rejected'
      | 'reverted';
    terminalStatus:
      | 'succeeded'
      | 'failed'
      | 'timed_out'
      | 'abandoned'
      | 'result_post_failed';
    source: 'plugin_result' | 'timeout' | 'user_reject';
    executionTimeMs?: number;
    errorType?: string;
    failureClass?:
      | 'lifecycle_noise'
      | 'agent_generated_code'
      | 'model_state'
      | 'template_or_platform';
    recoverable?: boolean;
    isFailureReported?: boolean;
    failureStage?:
      | 'queue'
      | 'dispatch'
      | 'compile'
      | 'execute'
      | 'upload'
      | 'result_post'
      | 'unknown';
  };
  rhino_tool_called: {
    toolName: string;
    requestId: string;
    chatId?: string;
    messageId?: string;
    toolCallId?: string;
    readOnly: boolean;
    isTemplate: boolean;
  };
  rhino_tool_completed: {
    toolName: string;
    requestId: string;
    chatId?: string;
    messageId?: string;
    toolCallId?: string;
    success: boolean;
    readOnly: boolean;
    approvalStatus:
      | 'not_required'
      | 'pending'
      | 'approved'
      | 'rejected'
      | 'reverted';
    terminalStatus:
      | 'succeeded'
      | 'failed'
      | 'timed_out'
      | 'abandoned'
      | 'result_post_failed';
    source: 'plugin_result' | 'timeout' | 'user_reject';
    executionTimeMs?: number;
    errorType?: string;
    failureClass?:
      | 'lifecycle_noise'
      | 'agent_generated_code'
      | 'model_state'
      | 'template_or_platform';
    recoverable?: boolean;
    isFailureReported?: boolean;
    failureStage?:
      | 'queue'
      | 'dispatch'
      | 'compile'
      | 'execute'
      | 'upload'
      | 'result_post'
      | 'unknown';
  };
  archicad_tool_called: {
    toolName: string;
    requestId: string;
    chatId?: string;
    messageId?: string;
    toolCallId?: string;
    readOnly: boolean;
    isTemplate: boolean;
  };
  archicad_tool_completed: {
    toolName: string;
    requestId: string;
    chatId?: string;
    messageId?: string;
    toolCallId?: string;
    success: boolean;
    readOnly: boolean;
    approvalStatus:
      | 'not_required'
      | 'pending'
      | 'approved'
      | 'rejected'
      | 'reverted';
    terminalStatus:
      | 'succeeded'
      | 'failed'
      | 'timed_out'
      | 'abandoned'
      | 'result_post_failed';
    source: 'plugin_result' | 'timeout' | 'user_reject';
    executionTimeMs?: number;
    errorType?: string;
    failureClass?:
      | 'lifecycle_noise'
      | 'agent_generated_code'
      | 'model_state'
      | 'template_or_platform';
    recoverable?: boolean;
    isFailureReported?: boolean;
    failureStage?:
      | 'queue'
      | 'dispatch'
      | 'compile'
      | 'execute'
      | 'upload'
      | 'result_post'
      | 'unknown';
  };
  revit_execution_dispatched: {
    toolName: string;
    requestId: string;
    chatId?: string;
    messageId?: string;
    toolCallId?: string;
    readOnly: boolean;
    approvalStatus: 'not_required' | 'approved';
  };
  revit_tool_blocked: {
    toolName: string;
    requestId: string;
    chatId?: string;
    messageId?: string;
    toolCallId?: string;
    readOnly: boolean;
    reason: 'pending_conflict';
  };
  revit_tool_result_ignored: {
    requestId: string;
    toolName?: string;
    chatId?: string;
    messageId?: string;
    toolCallId?: string;
    existingStatus: 'pending' | 'in_progress' | 'completed' | 'error';
    incomingStatus: 'completed' | 'error' | 'validation_fallback';
    reason: 'terminal_request' | 'timed_out_execution';
  };

  // Plugin silent auto-updater health (ENG-1249)
  // Captured from the updater-status object on the bridge auth_response, with
  // change-only dedupe (see lib/analytics/plugin-updater-status.ts). Rollout
  // health dashboards read `stagedVersion` (cohort), `machineId` (cohort size),
  // and `lastOutcome`/`lastVersion` (the "fraction reporting healthy on the new
  // version" numerator/denominator).
  plugin_update_status: {
    connector: HostConnector;
    /** Plugin version currently running — the rollout census dimension. */
    currentVersion?: string;
    machineId?: string;
    armed?: boolean;
    stagedVersion?: string;
    pendingVersion?: string;
    lastOutcome?: string;
    lastVersion?: string;
    /** Failure-only (plugin-gated): actionable detail, e.g. "HTTP 404 for <url>". */
    lastDetail?: string;
    /** Failure-only (plugin-gated): last ~20 updater.log lines. */
    logTail?: string;
  };

  // Web Search
  web_search_used: {
    chatId: string;
    resultCount: number;
  };
  web_source_clicked: {
    url: string;
    title?: string;
  };

  // Workflow Parameters
  'workflow_parameters.schema_invalid': {
    planTitle: string;
    reason: string;
  };
}

/** All valid event names, derived from the map */
export type AnalyticsEventName = keyof AnalyticsEventMap;

/**
 * Type-safe event capture.
 * Safely handles cases where PostHog is not initialized.
 *
 * @example
 * capture('project_created', { projectId: 'abc123' });
 * capture('chat_message_sent', { chatId: 'chat-456', messageLength: 150 });
 */
export function capture<E extends AnalyticsEventName>(
  event: E,
  properties: AnalyticsEventMap[E],
): void {
  // Guard against calling PostHog before initialization
  if (!isPostHogInitialized()) return;

  const platform = getClientAnalyticsPlatform();
  posthog.register({ platform });

  posthog.capture(event, {
    ...properties,
    ...(event === 'chat_message_sent' ? { platform } : {}),
  });
}
